import { entryLabel } from "../PivotTable/pivotModel";

// The specimens behind one count in a pivot report, as filter conditions: the
// row's and the column's value for each dimension, to AND with the report's
// own filters. Catch-alls become "is empty" conditions.

const shiftDay = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const lastOfMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

const between = (from, to) => ({ field: "report_date", operator: "between", value: [from, to] });
const hasSite = { field: "collection_site", operator: "is_not_empty" };
const noSite = { field: "collection_site", operator: "is_empty" };

/** Conditions for one dimension's value (an axis entry part), or null if none can be said. */
export const dimensionClauses = (dim, key, label) => {
  switch (dim) {
    case "species":
      return [{ field: "summary_species", operator: "is", value: label }];
    case "genus":
      return [{ field: "final_genus", operator: "is", value: label }];
    case "sex":
      return key === "" ? [{ field: "final_sex", operator: "is_empty" }] : [{ field: "final_sex", operator: "is", value: key }];
    case "collectionSite":
      return key === "" ? [noSite] : [{ field: "collection_site", operator: "is", value: key }];
    case "county":
      if (key === "") return [noSite];
      if (key === "~") return [hasSite, { field: "collection_site_county", operator: "is_empty" }];
      return [{ field: "collection_site_county", operator: "is", value: key }];
    case "siteGroup":
      if (key === "") return [noSite];
      if (key === "~") return [hasSite, { field: "site_group", operator: "is_empty" }];
      return [{ field: "site_group", operator: "is", value: key }];
    case "collectionMethod":
      return key === ""
        ? [{ field: "collection_method_display_name", operator: "is_empty" }]
        : [{ field: "collection_method_display_name", operator: "is", value: key }];
    case "day":
      return [between(key, key)];
    case "week":
      return [between(key, shiftDay(key, 6))];
    case "month":
      return [between(`${key}-01`, lastOfMonth(key))];
    case "year":
      return [between(`${key}-01-01`, `${key}-12-31`)];
    default:
      return null;
  }
};

/**
 * The conditions for a cell, {row, column} axis entries of a pivot whose
 * dimensions are `rowDims` and `colDims`; null when a dimension has no
 * condition to express it (the specimens cannot be listed).
 */
export const cellClauses = ({ row, column }, rowDims, colDims) => {
  const parts = [
    ...rowDims.map((d, i) => [d, row?.key?.[i], row?.label?.[i]]),
    ...colDims.map((d, i) => [d, column?.key?.[i], column?.label?.[i]]),
  ];
  const clauses = [];
  for (const [dim, key, label] of parts) {
    if (key === undefined) continue;
    const more = dimensionClauses(dim, key, label);
    if (!more) return null;
    clauses.push(...more);
  }
  return clauses;
};

/** "Culex pipiens · Alpha Marsh", what the list of a cell's specimens is. */
export const cellTitle = ({ row, column }) =>
  [row, column]
    .filter((e) => e?.label?.length)
    .map(entryLabel)
    .join(" · ");

/** A report filter body with more conditions ANDed in. */
export const withClauses = (filter, clauses) => ({
  ...filter,
  criteria: { op: "and", clauses: [...(filter.criteria ? [filter.criteria] : []), ...clauses] },
});
